import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { Button } from "@/components/ui/v2/button";
import { DialogFooter } from "@/components/ui/v2/dialog";
import { Input } from "@/components/ui/v2/input";
import { Label } from "@/components/ui/v2/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/v2/select";
import { Stepper } from "@/components/ui/v2/stepper";
import { Textarea } from "@/components/ui/v2/textarea";

const createCompanyRegistrationSchema = (t: (key: string, fallback: string) => string) =>
  z.object({
    companyName: z.string().min(1, t("integrations.registerCompany.validation.companyNameRequired", "Company name is required")),
    city: z.string().min(1, t("integrations.registerCompany.validation.cityRequired", "City is required")),
    description: z.string().min(1, t("integrations.registerCompany.validation.descriptionRequired", "Description is required")),
    companyPhone: z.string().optional(),
    websiteUrl: z.string().transform((v) => {
      if (!v) return v;
      return /^https?:\/\//i.test(v) ? v : `https://${v}`;
    }).pipe(z.string().url(t("integrations.registerCompany.validation.invalidUrl", "Must be a valid URL"))).optional().or(z.literal("")),
    companyType: z.string().optional(),
    contactName: z.string().min(1, t("integrations.registerCompany.validation.contactNameRequired", "Contact name is required")),
    contactEmail: z.string().email(t("integrations.registerCompany.validation.invalidEmail", "Must be a valid email address")),
    contactPhone: z.string().optional(),
  });

export type CompanyRegistrationFormValues = z.infer<ReturnType<typeof createCompanyRegistrationSchema>>;

type UseCompanyRegistrationFormOptions = {
  onSubmit: (values: CompanyRegistrationFormValues) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  integrationDisplayName: string;
  disabled?: boolean;
};

const STEP_1_FIELDS = ["companyName", "city", "description", "companyPhone", "websiteUrl", "companyType"] as const;

export function useCompanyRegistrationForm({
  onSubmit,
  onCancel,
  isSubmitting,
  integrationDisplayName,
  disabled = false,
}: UseCompanyRegistrationFormOptions) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);

  const schema = createCompanyRegistrationSchema(t);

  const form = useForm<CompanyRegistrationFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      companyName: "",
      city: "",
      description: "",
      companyPhone: "",
      websiteUrl: "",
      companyType: "",
      contactName: "",
      contactEmail: "",
      contactPhone: "",
    },
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    trigger,
    formState: { errors },
  } = form;

  const handleContinue = async () => {
    const valid = await trigger(STEP_1_FIELDS as unknown as (keyof CompanyRegistrationFormValues)[]);
    if (valid) setStep(2);
  };

  const handleRegisterClick = async () => {
    const allValid = await trigger();
    if (allValid) {
      handleSubmit(onSubmit)();
      return;
    }
    const currentErrors = form.formState.errors;
    const step1HasErrors = STEP_1_FIELDS.some((f) => f in currentErrors);
    if (step1HasErrors) setStep(1);
  };

  const formElement = (
    <div className={`flex flex-col gap-4 transition-opacity ${disabled ? "pointer-events-none opacity-40" : ""}`}>
      <Stepper
        steps={[
          { title: t("integrations.registerCompany.companyDetails", "Company Details") },
          { title: t("integrations.registerCompany.contactPerson", "Contact Person") },
        ]}
        currentStep={step - 1}
      />

      {/* Step 1: Company Details */}
      {step === 1 && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reg-companyName">
              {t("integrations.registerCompany.companyName", "Company Name")} <span className="text-destructive">*</span>
            </Label>
            <Input id="reg-companyName" {...register("companyName")} placeholder={t("integrations.registerCompany.companyNamePlaceholder", "Enter company name")} />
            {errors.companyName && <p className="text-sm text-destructive">{errors.companyName.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reg-city">
                {t("integrations.registerCompany.city", "City")} <span className="text-destructive">*</span>
              </Label>
              <Input id="reg-city" {...register("city")} placeholder={t("integrations.registerCompany.cityPlaceholder", "Enter city")} />
              {errors.city && <p className="text-sm text-destructive">{errors.city.message}</p>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reg-companyType">
                {t("integrations.registerCompany.companyType", "Company Type")} <span className="text-muted-foreground font-normal">({t("common.optional", "optional")})</span>
              </Label>
              <Select value={watch("companyType") || ""} onValueChange={(value) => setValue("companyType", value)}>
                <SelectTrigger id="reg-companyType">
                  <SelectValue placeholder={t("integrations.registerCompany.companyTypePlaceholder", "Select type")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="direct">{t("integrations.registerCompany.typeDirect", "Direct employer")}</SelectItem>
                  <SelectItem value="staffing">{t("integrations.registerCompany.typeStaffing", "Staffing agency")}</SelectItem>
                  <SelectItem value="agency">{t("integrations.registerCompany.typeAgency", "Recruitment agency")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reg-description">
              {t("integrations.registerCompany.description", "Description")} <span className="text-destructive">*</span>
            </Label>
            <Textarea id="reg-description" {...register("description")} placeholder={t("integrations.registerCompany.descriptionPlaceholder", "Describe the company's activities, mission, vision, etc.")} rows={3} />
            {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reg-companyPhone">
                {t("integrations.registerCompany.companyPhone", "Company Phone")} <span className="text-muted-foreground font-normal">({t("common.optional", "optional")})</span>
              </Label>
              <Input id="reg-companyPhone" type="tel" {...register("companyPhone")} placeholder="0101234567" />
              <p className="text-xs text-muted-foreground">{t("integrations.registerCompany.companyPhoneHint", "Company contact number, e.g. 0101234567")}</p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reg-websiteUrl">
                {t("integrations.registerCompany.websiteUrl", "Website")} <span className="text-muted-foreground font-normal">({t("common.optional", "optional")})</span>
              </Label>
              <Input id="reg-websiteUrl" type="url" {...register("websiteUrl")} placeholder="https://example.com" />
              {errors.websiteUrl && <p className="text-sm text-destructive">{errors.websiteUrl.message}</p>}
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Contact Person */}
      {step === 2 && (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reg-contactName">
                {t("integrations.registerCompany.contactName", "Name")} <span className="text-destructive">*</span>
              </Label>
              <Input id="reg-contactName" {...register("contactName")} placeholder={t("integrations.registerCompany.contactNamePlaceholder", "Enter contact name")} />
              {errors.contactName && <p className="text-sm text-destructive">{errors.contactName.message}</p>}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="reg-contactPhone">
                {t("integrations.registerCompany.contactPhone", "Contact Phone")} <span className="text-muted-foreground font-normal">({t("common.optional", "optional")})</span>
              </Label>
              <Input id="reg-contactPhone" type="tel" {...register("contactPhone")} placeholder="0651234567" />
              <p className="text-xs text-muted-foreground">{t("integrations.registerCompany.contactPhoneHint", "Personal phone of the contact, e.g. 0651234567")}</p>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="reg-contactEmail">
              {t("integrations.registerCompany.contactEmail", "Email")} <span className="text-destructive">*</span>
            </Label>
            <Input id="reg-contactEmail" type="email" {...register("contactEmail")} placeholder={t("integrations.registerCompany.contactEmailPlaceholder", "Enter contact email")} />
            {errors.contactEmail && <p className="text-sm text-destructive">{errors.contactEmail.message}</p>}
            <p className="text-xs text-muted-foreground">
              {t(
                "integrations.registerCompany.activationEmailNotice",
                "An activation email will be sent to this address. The contact must activate the account before it is active and ready to use at {{integrationName}}.",
                { integrationName: integrationDisplayName }
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );

  const footerElement = (
    <DialogFooter className="sm:justify-between">
      <Button
        type="button"
        variant="outline"
        onClick={step > 1 ? () => setStep(1) : onCancel}
        disabled={isSubmitting}
      >
        {step > 1
          ? `← ${t("common.back", "Back")}`
          : t("common.cancel", "Cancel")}
      </Button>
      {step === 1 ? (
        <Button type="button" onClick={handleContinue} disabled={disabled}>
          {t("common.continue", "Continue")}
        </Button>
      ) : (
        <Button type="button" disabled={isSubmitting || disabled} onClick={handleRegisterClick}>
          {isSubmitting && <Loader2 className="mr-2 size-4 animate-spin" />}
          {t("integrations.registerCompany.register", "Register Company")}
        </Button>
      )}
    </DialogFooter>
  );

  return { formElement, footerElement, step, setStep };
}
